import { currencyCodeSuffix } from '../../utils/money';
import { useBookingLocale } from '../../hooks/useBookingLocale';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import tw from 'twin.macro';
import { BookingStatus } from '../../../__generated__/graphql';
import { Button } from '../../components/button';
import { Modal } from '../../components/modal';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardHeader,
  DD,
  DescriptionList,
  DT,
  Field,
  InlineRow,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  Stack,
  Strong,
  Textarea,
} from '../../components/ui';
import {
  useCancelJobMutation,
  useJobCancellationPreviewQuery,
  useJobTimelineQuery,
  useServiceJobAsProviderQuery,
} from '../../services/serviceService/__generated__/queries.generated';
import {
  jobItemLabels,
  jobStatusView,
  windowText,
  type ServiceJob,
} from '../ServiceGuestPage/serviceJob';
import { JobAccessCard } from '../ServiceJobPage/JobAccessCard';
import { HandoverClock } from '../ServiceJobPage/HandoverClock';
import { JobTimelineView } from '../ServiceJobPage/JobTimelineView';
import { ServiceReviewCard } from '../ServiceJobPage/ServiceReviewCard';
import { completedAtOf, strikesOf, type StrikeView } from '../ServiceJobPage/jobTimeline';
import { WorkerJobControls } from '../ServiceJobPage/WorkerJobControls';
import { stayErrorMessage } from '../StayHostPage/stayErrors';
import { formatMoney } from './priceTables';
import { ProviderJobCard } from './ProviderJobCard';
import { PROVIDER_HOME_PATH } from './servicePaths';
import { StrikeAppealModal } from './StrikeAppealModal';
import { STRIKE_STATE_VIEW } from './WorkerStrikesCard';

const Columns = styled.div`
  ${tw`grid grid-cols-1 gap-6 lg:grid-cols-3`}
`;

const Main = styled.div`
  ${tw`flex flex-col gap-6 lg:col-span-2 min-w-0`}
`;

const Side = styled.div`
  ${tw`flex flex-col gap-6 min-w-0`}
`;

function ProviderCancelModal({ job, onClose }: { job: ServiceJob; onClose: () => void }) {
  const preview = useJobCancellationPreviewQuery({
    variables: { bookingId: job.id },
    fetchPolicy: 'network-only',
  });
  const [cancelJob, { loading }] = useCancelJobMutation();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const p = preview.data?.jobCancellationPreview;
  return (
    <Modal isOpen onClose={onClose} title="Cancel this job">
      <Stack $gap={4}>
        <Strong>Cancel this job?</Strong>
        {p ? (
          <Muted>
            {p.message}
            {p.countsAsLateCancel ? ' This counts as a late cancellation for the worker.' : ''}
          </Muted>
        ) : preview.error ? (
          <Alert tone="error" title="Cannot cancel">
            {stayErrorMessage(preview.error)}
          </Alert>
        ) : (
          <LoadingBlock label="Checking" />
        )}
        <Field label="Reason for the customer" htmlFor="provider-cancel-reason" required>
          <Textarea
            id="provider-cancel-reason"
            rows={2}
            maxLength={500}
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
        </Field>
        {error && (
          <Alert tone="error" title="Not cancelled">
            {error}
          </Alert>
        )}
        <InlineRow>
          <Button
            type="button"
            theme="danger"
            text="Cancel job"
            isLoading={loading}
            isDisabled={!p || !reason.trim()}
            onClick={async () => {
              try {
                await cancelJob({ variables: { bookingId: job.id, reason: reason.trim() } });
                toast.success('Job cancelled. The customer is not charged.');
                onClose();
              } catch (e) {
                setError(stayErrorMessage(e, 'Could not cancel'));
              }
            }}
          />
          <Button type="button" theme="text" text="Keep it" onClick={onClose} />
        </InlineRow>
      </Stack>
    </Modal>
  );
}

/**
 * One job for its Provider (spec 019 W18): accept or decline a request, the
 * address and door code, the worker's check-ins (the Provider can check in
 * for a worker), the timeline, strikes with an appeal (D17), and cancel.
 */
export default function ProviderJobPage() {
  const { bookingId = '' } = useParams<{ bookingId: string }>();
  const jobLocale = useBookingLocale(bookingId, 'host');
  const jobQuery = useServiceJobAsProviderQuery({
    variables: { id: bookingId },
    fetchPolicy: 'cache-and-network',
  });
  const job = jobQuery.data?.getBookingAsHost ?? null;
  const booked = job?.status === BookingStatus.Booked;
  const timelineQuery = useJobTimelineQuery({
    variables: { bookingId },
    skip: !job || job.status === BookingStatus.Requested,
    fetchPolicy: 'cache-and-network',
  });
  const [appealing, setAppealing] = useState<StrikeView | null>(null);
  const [cancelling, setCancelling] = useState(false);

  if (jobQuery.loading && !job) return <LoadingBlock label="Loading the job" />;
  if (!job || !job.serviceJob) {
    return (
      <PageContainer width="md">
        <Alert tone="error" title="Job not found">
          {jobQuery.error ? stayErrorMessage(jobQuery.error) : 'This job could not be loaded.'}{' '}
          <Link to={PROVIDER_HOME_PATH}>Today</Link>
        </Alert>
      </PageContainer>
    );
  }
  const info = job.serviceJob;
  const status = jobStatusView(job);
  const timeline = timelineQuery.data?.jobTimeline ?? null;
  const strikes = timeline ? strikesOf(timeline.events) : [];

  return (
    <PageContainer width="xl" canvas data-testid="provider-job">
      <PageHeader
        eyebrow="Job"
        title={jobItemLabels(info.items).join(' + ') || 'Job'}
        description={windowText(info.windowStartAt, info.windowEndAt, info.timezone)}
        backTo={{ to: PROVIDER_HOME_PATH, label: 'Today' }}
        actions={<Badge tone={status.tone}>{status.label}</Badge>}
      />
      <Columns>
        <Main>
          {job.status === BookingStatus.Requested && <ProviderJobCard job={job} />}
          {strikes.length > 0 && (
            <Card data-testid="job-strikes">
              <CardHeader
                title="Strikes"
                description="A late cancellation or a no-show counts against the worker."
              />
              <CardBody>
                <Stack $gap={4}>
                  {strikes.map(strike => (
                    <InlineRow
                      key={strike.event.id}
                      style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}
                    >
                      <span>{strike.label}</span>
                      <InlineRow>
                        <Badge tone={STRIKE_STATE_VIEW[strike.state].tone}>
                          {STRIKE_STATE_VIEW[strike.state].label}
                        </Badge>
                        {strike.state === 'open' && (
                          <Button
                            type="button"
                            theme="secondary"
                            size="sm"
                            text="Appeal"
                            onClick={() => setAppealing(strike)}
                          />
                        )}
                      </InlineRow>
                    </InlineRow>
                  ))}
                </Stack>
              </CardBody>
            </Card>
          )}
          {timeline && (
            <HandoverClock
              minutesSoFar={timeline.handoverMinutesSoFar}
              maxBillableMinutes={info.maxBillableMinutes}
            />
          )}
          {booked && timeline && (
            <WorkerJobControls
              bookingId={job.id}
              timeline={timeline}
              onRecorded={() => jobQuery.refetch()}
            />
          )}
          {timeline && (
            <ServiceReviewCard
              bookingId={job.id}
              reviewer="worker"
              jobStatus={timeline.jobStatus}
              completedAt={info.completedAt ?? completedAtOf(timeline.events)}
              otherName={job.guestDisplayName ?? 'the customer'}
              zone={info.timezone}
            />
          )}
          {timeline && <JobTimelineView timeline={timeline} zone={info.timezone} />}
        </Main>
        <Side>
          {booked && <JobAccessCard bookingId={job.id} zone={info.timezone} />}
          <Card>
            <CardHeader title="Booking" />
            <CardBody>
              <DescriptionList>
                <DT>Worker</DT>
                <DD>{info.workerName ?? 'Not set'}</DD>
                <DT>Customer</DT>
                <DD>{job.guestDisplayName ?? 'Customer'}</DD>
                <DT>Where</DT>
                <DD>
                  {[info.site?.addressLine1, `${info.site?.city ?? ''} ${info.site?.zip ?? ''}`]
                    .filter(Boolean)
                    .join(', ')}
                </DD>
                <DT>Total</DT>
                <DD>
                  {formatMoney(Number(job.receipt.finalTotal ?? job.receipt.totalPrice ?? 0))}
                  {currencyCodeSuffix(jobLocale.currency)}
                </DD>
                {info.customerNote && (
                  <>
                    <DT>Note</DT>
                    <DD>{info.customerNote}</DD>
                  </>
                )}
              </DescriptionList>
            </CardBody>
          </Card>
          {booked && !info.completedAt && (
            <Button
              type="button"
              theme="secondary"
              text="Cancel job"
              onClick={() => setCancelling(true)}
              dataTestId="provider-cancel"
            />
          )}
        </Side>
      </Columns>
      {appealing && (
        <StrikeAppealModal
          eventId={appealing.event.id}
          label={appealing.label}
          onClose={() => setAppealing(null)}
        />
      )}
      {cancelling && <ProviderCancelModal job={job} onClose={() => setCancelling(false)} />}
    </PageContainer>
  );
}
